import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useAuth0 } from './useAuthPatched';
import axios from 'axios';
import { getApiUrl } from '../config';

// Create context for profile data
const UserProfileContext = createContext({
    profile: null,
    loading: true,
    error: null,
    isProfileComplete: false,
    missingFields: [],
    refreshProfile: () => {},
    updateProfile: () => Promise.resolve()
});

// Provider component
export const UserProfileProvider = ({ children }) => {
    const { user, getAccessTokenSilently, isAuthenticated } = useAuth0();
    const [state, setState] = useState({
        profile: null,
        loading: true,
        error: null,
        isProfileComplete: false,
        missingFields: []
    });

    // Check if profile is complete
    const checkProfileCompletion = useCallback((profile) => {
        if (!profile) return { isComplete: false, missing: ['firstName', 'lastName', 'email', 'phone'] };

        const requiredFields = {
            firstName: 'First Name',
            lastName: 'Last Name',
            email: 'Email',
            phone: 'Phone Number',
            address: 'Street Address',
            city: 'City',
            state: 'State',
            zip: 'ZIP Code'
        };

        const missingFields = [];
        Object.entries(requiredFields).forEach(([field, label]) => {
            if (!profile[field] || profile[field].trim() === '') {
                missingFields.push({ field, label });
            }
        });

        return {
            isComplete: missingFields.length === 0,
            missing: missingFields
        };
    }, []);

    // Fetch user profile
    const fetchProfile = useCallback(async () => {
        if (!isAuthenticated || !user) {
            setState(prev => ({ ...prev, loading: false }));
            return;
        }

        try {
            setState(prev => ({ ...prev, loading: true, error: null }));
            
            const token = await getAccessTokenSilently();
            const response = await axios.get(`${getApiUrl('javaBackend')}/api/user/profile`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const profile = response.data;
            const { isComplete, missing } = checkProfileCompletion(profile);

            setState({
                profile,
                loading: false,
                error: null,
                isProfileComplete: isComplete,
                missingFields: missing
            });

            // Store profile completion status in localStorage for quick checks
            localStorage.setItem('profileComplete', isComplete.toString());
            localStorage.setItem('profileLastChecked', Date.now().toString());
            
        } catch (error) {
            console.error('Error fetching profile:', error);
            
            // If profile doesn't exist, create initial profile from Auth0 data
            if (error.response?.status === 404 && user) {
                const initialProfile = {
                    userId: user.sub,
                    firstName: user.given_name || '',
                    lastName: user.family_name || '',
                    email: user.email || '',
                    phone: '',
                    address: '',
                    city: '',
                    state: '',
                    zip: ''
                };

                const { isComplete, missing } = checkProfileCompletion(initialProfile);

                setState({
                    profile: initialProfile,
                    loading: false,
                    error: null,
                    isProfileComplete: isComplete,
                    missingFields: missing
                });
            } else {
                setState(prev => ({
                    ...prev,
                    loading: false,
                    error: error.message
                }));
            }
        }
    }, [isAuthenticated, user, getAccessTokenSilently, checkProfileCompletion]);

    // Update profile
    const updateProfile = useCallback(async (updatedData) => {
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.put(
                `${getApiUrl('javaBackend')}/api/user/profile`,
                updatedData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const updatedProfile = response.data;
            const { isComplete, missing } = checkProfileCompletion(updatedProfile);

            setState({
                profile: updatedProfile,
                loading: false,
                error: null,
                isProfileComplete: isComplete,
                missingFields: missing
            });

            // Update localStorage
            localStorage.setItem('profileComplete', isComplete.toString());
            localStorage.setItem('profileLastChecked', Date.now().toString());

            return { success: true, profile: updatedProfile };
        } catch (error) {
            console.error('Error updating profile:', error);
            return { 
                success: false, 
                error: error.response?.data?.message || 'Failed to update profile' 
            };
        }
    }, [getAccessTokenSilently, checkProfileCompletion]);

    // Initial load and refresh on auth change
    useEffect(() => {
        // Check localStorage first for quick initial state
        const profileComplete = localStorage.getItem('profileComplete');
        const lastChecked = localStorage.getItem('profileLastChecked');
        const oneHourAgo = Date.now() - (60 * 60 * 1000);

        // If we have recent data, use it for initial state
        if (profileComplete && lastChecked && parseInt(lastChecked) > oneHourAgo) {
            setState(prev => ({
                ...prev,
                isProfileComplete: profileComplete === 'true',
                loading: false
            }));
        }

        // Then fetch fresh data
        fetchProfile();
    }, [fetchProfile]);

    const contextValue = {
        profile: state.profile,
        loading: state.loading,
        error: state.error,
        isProfileComplete: state.isProfileComplete,
        missingFields: state.missingFields,
        refreshProfile: fetchProfile,
        updateProfile
    };

    return (
        <UserProfileContext.Provider value={contextValue}>
            {children}
        </UserProfileContext.Provider>
    );
};

// Hook to use profile context
export const useUserProfile = () => {
    const context = useContext(UserProfileContext);
    if (!context) {
        throw new Error('useUserProfile must be used within UserProfileProvider');
    }
    return context;
};

// Standalone hook for components that don't need the full context
export const useProfileCompletionCheck = () => {
    const { isAuthenticated, user } = useAuth0();
    const [isComplete, setIsComplete] = useState(true);

    useEffect(() => {
        // Quick check from localStorage
        const profileComplete = localStorage.getItem('profileComplete');
        if (profileComplete !== null) {
            setIsComplete(profileComplete === 'true');
        }
    }, [isAuthenticated, user]);

    return isComplete;
};
